/**
 * Scanned items: the strip of what you are holding, and where it would place.
 *
 * These sit above the catalogue comparison rather than inside it, because they
 * are a different KIND of thing and saying so is more useful than blending
 * them in. A catalogue row is a price someone can go and pay; a scanned row is
 * a packet in the reader's hand, priced by them, read by a camera. Presenting
 * the two identically would quietly claim a confidence the scan has not
 * earned.
 *
 * Nothing here is persisted. Closing the tab ends these items' existence.
 */

import { useMemo, useState } from 'react';
import {
  computeMetrics,
  METRICS,
  METRIC_DIRECTION,
  type MetricId,
} from '@/lib/nutrition/calculations';
import { formatMetricValue, metricUnit } from '@/lib/format';
import type { Product } from '@/types/product';
import type { ScanEntry, ScannedItem } from '@/lib/scanner/calculate';
import {
  describePlacement,
  placeAgainst,
  PREVIEW_BOARDS,
  type PreviewPlacement,
} from '@/lib/scanner/ranking-preview';

/** Everything a scanned label can support, in the order people ask for it. */
const SCAN_METRICS: MetricId[] = [
  'kcal100',
  'protein100',
  'carb100',
  'fat100',
  'proteinPer100kcal',
  'carbsPer100kcal',
  'fatPer100kcal',
  'proteinPctCalories',
  'carbPctCalories',
  'fatPctCalories',
  'proteinPerDollar',
  'kcalPerDollar',
  'carbsPerDollar',
  'fatPerDollar',
  'pricePer100g',
  'pricePer100ml',
  'pricePer100kcal',
  'pricePer10gProtein',
  'fibre100',
  'sugar100',
  'sodium100',
];

export function ScannedItems({
  items,
  catalogue,
  eligible,
  onRemove,
  onUpdate,
}: {
  items: ScannedItem[];
  /** Ranked catalogue products the app already holds, for the placement. */
  catalogue: Product[];
  eligible: number | null;
  onRemove: (id: string) => void;
  onUpdate: (id: string, entry: ScanEntry) => void;
}): React.JSX.Element | null {
  if (items.length === 0) return null;

  return (
    <section className="scanned">
      <div className="scanned-head">
        <h2>Scanned items ({items.length})</h2>
        {/*
          Kept because it is behaviour the reader has to know: a scan is not
          kept between visits, and someone who expects to find it again would
          lose their work. It says what happens to the item, not where the
          processing occurs.
        */}
        <p className="scanned-note">
          These disappear when you close the page.
        </p>
      </div>

      <div className="scanned-grid">
        {items.map((item) => (
          <ScannedCard
            key={item.id}
            item={item}
            catalogue={catalogue}
            eligible={eligible}
            onRemove={() => onRemove(item.id)}
            onUpdate={(entry) => onUpdate(item.id, entry)}
          />
        ))}
      </div>

      {items.length === 2 && <HeadToHead a={items[0]!} b={items[1]!} />}
    </section>
  );
}

function ScannedCard({
  item,
  catalogue,
  eligible,
  onRemove,
  onUpdate,
}: {
  item: ScannedItem;
  catalogue: Product[];
  eligible: number | null;
  onRemove: () => void;
  onUpdate: (entry: ScanEntry) => void;
}): React.JSX.Element {
  const [editing, setEditing] = useState(false);
  const metrics = useMemo(() => computeMetrics(item.product), [item.product]);

  const placements = useMemo(
    () =>
      PREVIEW_BOARDS.map((board) => placeAgainst(item.product, catalogue, board, eligible)).filter(
        (p): p is PreviewPlacement => p !== null,
      ),
    [item.product, catalogue, eligible],
  );

  const currency = metrics.currency;
  const available = SCAN_METRICS.filter((m) => metrics.values[m]?.value != null);

  return (
    <article className="scanned-card">
      <header className="scanned-card-head">
        <h3>{item.product.name}</h3>
        <button type="button" className="btn btn-sm" onClick={() => setEditing((v) => !v)}>
          {editing ? 'Done' : 'Edit'}
        </button>
        <button type="button" className="btn btn-sm" onClick={onRemove}>
          Remove
        </button>
      </header>

      {/*
        Price and pack size are editable without rescanning, because they are
        what changes: a different shop, a promotion, the larger bag. Re-reading
        a label to change a number the user typed would be absurd.
      */}
      {editing && (
        <div className="scanned-edit">
          <label className="scan-field">
            <span className="scan-field-label">Price</span>
            <input
              type="number"
              inputMode="decimal"
              min={0}
              step="any"
              value={item.entry.price ?? ''}
              onChange={(e) =>
                onUpdate({
                  ...item.entry,
                  price: e.target.value === '' ? null : Number(e.target.value),
                })
              }
            />
          </label>
          <label className="scan-field">
            <span className="scan-field-label">Pack size</span>
            <input
              type="number"
              inputMode="decimal"
              min={0}
              step="any"
              value={item.entry.packAmount ?? ''}
              onChange={(e) =>
                onUpdate({
                  ...item.entry,
                  packAmount: e.target.value === '' ? null : Number(e.target.value),
                })
              }
            />
          </label>
          <label className="scan-field scan-field-narrow">
            <span className="scan-field-label">Unit</span>
            <select
              value={item.entry.packUnit}
              onChange={(e) =>
                onUpdate({ ...item.entry, packUnit: e.target.value as 'g' | 'ml' })
              }
            >
              <option value="g">g</option>
              <option value="ml">ml</option>
            </select>
          </label>
          <label className="scan-field">
            <span className="scan-field-label">Name</span>
            <input
              type="text"
              maxLength={40}
              value={item.entry.nickname ?? ''}
              onChange={(e) => onUpdate({ ...item.entry, nickname: e.target.value })}
            />
          </label>
        </div>
      )}

      {item.product.price === null && (
        <p className="scanned-warn">
          No price entered, so the per-dollar figures cannot be worked out.
        </p>
      )}
      {item.product.quantity === null && (
        <p className="scanned-warn">
          No pack size entered, so anything about the whole pack is unavailable.
        </p>
      )}

      {/* Scrollable rather than truncated: the user asked to see everything
          that can be calculated, so nothing is hidden behind a "more" link. */}
      <div className="scanned-metrics">
        {available.length === 0 ? (
          <p className="scanned-warn">Not enough was read to calculate anything yet.</p>
        ) : (
          <dl>
            {available.map((metric) => (
              <div className="scanned-metric" key={metric}>
                <dt>
                  {METRICS[metric].label}
                  <span>{metricUnit(metric, currency)}</span>
                </dt>
                <dd>
                  {formatMetricValue(
                    metric,
                    metrics.values[metric]!,
                    currency,
                    metrics.packageKind,
                  )}
                </dd>
              </div>
            ))}
          </dl>
        )}
      </div>

      {placements.length > 0 && (
        <div className="scanned-rank">
          <h4>If it were in the catalogue</h4>
          <ul>
            {placements.map((p) => (
              <li key={p.boardId}>
                <span className="scanned-rank-board">{p.label}</span>
                <span className={`scanned-rank-place${p.beatsAll ? ' is-top' : ''}`}>
                  {describePlacement(p)}
                </span>
              </li>
            ))}
          </ul>
          {/*
            The exact scope of the claim, stated rather than implied.
            Comparing against the loaded rankings is a real comparison, but it
            is not the whole catalogue, and pretending otherwise would invent
            precision the data does not have.
          */}
          <p className="scanned-rank-note">
            Compared against the ranked products currently loaded
            {eligible !== null && `, out of ${eligible.toLocaleString()} the board can rank`}. Open
            a ranking first to compare against more of them.
          </p>
        </div>
      )}
    </article>
  );
}

/** Two scans side by side — which wins each metric, and by how much. */
function HeadToHead({ a, b }: { a: ScannedItem; b: ScannedItem }): React.JSX.Element {
  const ma = computeMetrics(a.product);
  const mb = computeMetrics(b.product);
  const currency = ma.currency ?? mb.currency;

  const rows = SCAN_METRICS.filter(
    (m) => ma.values[m]?.value != null || mb.values[m]?.value != null,
  );

  return (
    <div className="scanned-versus">
      <h3>Scanned side by side</h3>
      <div className="compare-scroll">
        <table className="compare-table">
          <thead>
            <tr>
              <th scope="col">Metric</th>
              <th scope="col">{a.product.name}</th>
              <th scope="col">{b.product.name}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((metric) => {
              const va = ma.values[metric]?.value ?? null;
              const vb = mb.values[metric]?.value ?? null;
              // A winner is only marked when BOTH sides have a value; one
              // number beats nothing by default, which is not a comparison.
              const both = va !== null && vb !== null;
              // Direction comes from the engine, so this table can never
              // disagree with the rankings about what 'better' means.
              const higher = METRIC_DIRECTION[metric] === 'higher';
              const aWins = both && (higher ? va > vb : va < vb);
              const bWins = both && (higher ? vb > va : vb < va);

              return (
                <tr key={metric}>
                  <th scope="row">
                    {METRICS[metric].label}
                    <span className="compare-formula">{metricUnit(metric, currency)}</span>
                  </th>
                  <td className={aWins ? 'is-best' : undefined}>
                    {aWins && <span className="best-mark" aria-label="Better">★</span>}
                    {va === null ? (
                      <span className="na">N/A</span>
                    ) : (
                      formatMetricValue(metric, ma.values[metric]!, currency, ma.packageKind)
                    )}
                  </td>
                  <td className={bWins ? 'is-best' : undefined}>
                    {bWins && <span className="best-mark" aria-label="Better">★</span>}
                    {vb === null ? (
                      <span className="na">N/A</span>
                    ) : (
                      formatMetricValue(metric, mb.values[metric]!, currency, mb.packageKind)
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
